import { Component, computed, DestroyRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideIcons } from '@ng-icons/core';
import { lucideEllipsisVertical, lucideMousePointerClick, lucidePen, lucidePlus, lucideTrash } from '@ng-icons/lucide';
import { DialogConfirmDeleteComponent } from '@shared/components/dialog/confirm-delete/dialog-confirm-delete.component';
import { ConfirmDialogComponent } from '@shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { ExpansionPanelMenuAction } from '@shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '@shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '@shared/components/toastr/toastr-success.component';
import { TrierParPipe } from '@shared/pipes/trier-par.pipe';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmTabsImports } from '@spartan-ng/helm/tabs';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { CdkDragDrop, CdkDropList, CdkDropListGroup, moveItemInArray } from '@angular/cdk/drag-drop';
import { Cartographie } from '../../../cartographie/models/cartographie.model';
import { CardItemImports } from '../../../shared/components/card-item';
import { ProductionStore } from '../../référentiel-métier-ressources-professionnelles/store/production.store';
import { FormIndiceComponent } from '../processus-indice-resultats/form/form-indice/form-indice.component';
import { ItemIndiceResultatsComponent } from '../processus-indice-resultats/item-indice-resultats/item-indice-resultats.component';
import { IndiceRésultats } from '../processus-indice-resultats/models/indice-résultats';
import { IndiceRésultatsStore } from '../processus-indice-resultats/store/indice-résultats.store';
import { Phase } from '../processus-phases/models/phase.model';
import { PhaseStore } from '../processus-phases/store/phase.store';
import { ProductionFormComponent } from '../processus-production/forms/production-form/production-form.component';
import { Aptitude } from './models/aptitude.model';
import { Production } from './models/production.model';
import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-processus-aptitude',
  imports: [
    HlmTabsImports,
    HlmIconImports,
    HlmButtonImports,
    CardItemImports,
    HlmDropdownMenuImports,
    TrierParPipe,
    ItemIndiceResultatsComponent,
    CdkDropListGroup,
    CdkDropList,
  ],
  templateUrl: './processus-aptitude.component.html',
  providers: [provideIcons({ lucideMousePointerClick, lucidePlus, lucideEllipsisVertical, lucidePen, lucideTrash })],
})
export class ProcessusAptitudeComponent {
  readonly #phaseStore = inject(PhaseStore);
  readonly #indiceStore = inject(IndiceRésultatsStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #productionStore = inject(ProductionStore);

  readonly item = input.required<Aptitude>({ alias: 'aptitude' });
  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();
  readonly phases = input.required<Phase[]>();
  readonly peutModifier = input.required<boolean>();

  readonly onCreateProduction = output<void>();
  readonly onCreateIndice = output<void>();

  readonly phase = computed(() => (this.item() ? this.#phaseStore.entityMap()[this.item()!.idPhase] : undefined));

  readonly productions = computed(() =>
    this.#productionStore.entities().filter((x) => x.idAptitude === this.item().id),
  );
  readonly indicesResultats = computed(() =>
    this.#indiceStore.entities().filter((x) => x.idAptitude === this.item().id),
  );

  readonly actionsProduction: ExpansionPanelMenuAction<Production>[] = [
    { icon: 'lucidePen', label: 'Modifier la production', action: (item) => this.#modifierProduction(item) },
    { icon: 'lucideTrash', label: 'Supprimer la production', action: (item) => this.#supprimerProduction(item) },
  ];

  readonly actionsIndice: ExpansionPanelMenuAction<IndiceRésultats>[] = [
    { icon: 'lucidePen', label: "Modifier l'indice de résultat", action: (item) => this.#modifierIndiceResultat(item) },
    {
      icon: 'lucideTrash',
      label: "Supprimer l'indice de résultat",
      action: (item) => this.#supprimerIndiceResultat(item),
    },
  ];

  createProduction = () => this.onCreateProduction.emit();
  createIndice = () => this.onCreateIndice.emit();

  #modifierProduction(production: Production | undefined) {
    if (!production) return;

    this.#sidepanelService.setComponentToDisplay(
      'Éditer une production',
      ProductionFormComponent,
      `Modifier le nom et/ou l'aptitude de la production.`,
      { production: production, idAptitude: this.item().id, cartographie: this.cartographie() },
    );
    this.#sidepanelService.open();
  }

  #supprimerProduction(production: Production | undefined) {
    if (!production) return;

    const nbRessources = production.ressources?.length ?? 0;
    const toastrId = uuidv7();

    let dialogRef;

    if (nbRessources) {
      dialogRef = this.#dialog.open(DialogConfirmDeleteComponent, {
        context: {
          nom: production.nom,
          childrenText: `${nbRessources} ressources professionnelles liées`,
        },
      });
    } else {
      dialogRef = this.#dialog.open(ConfirmDialogComponent, {
        context: {
          titre: `Supprimer la production`,
          contenu: `Êtes-vous sûr de vouloir supprimer la production <span class="font-semibold">${production.nom}</span> ?`,
          confirmBtnText: `Confirmer la suppression`,
        },
      });
    }

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        this.#productionStore.supprimer({
          idProduction: production.id,
          next: () => {
            toast.custom(ToastrSuccessComponent, {
              componentProps: {
                message: 'Suppression de la production réussie',
                id: toastrId,
              },
              id: toastrId,
            });
          },
        });
      }
    });
  }

  #modifierIndiceResultat(indiceResultats: IndiceRésultats | undefined): void {
    if (!indiceResultats) return;

    this.#sidepanelService.setComponentToDisplay(
      "Modifier l'indice de résultat",
      FormIndiceComponent,
      `Modifiez le nom et le parent de votre indice de résultats. Si besoin de modifier les indicateurs de performance rattachés, cliquez sur “gérer les indicateurs de performance” dans le sous-menu de l'indice de résultats.`,
      {
        indiceResultats,
        referentiel: this.referentiel(),
        phases: this.phases(),
      },
    );
    this.#sidepanelService.open();
  }

  #supprimerIndiceResultat(indice: IndiceRésultats | undefined): void {
    if (!indice) return;
    let dialogRef;

    const nbrIndicateurs = indice.indicateursPerformance.length;
    if (nbrIndicateurs > 0) {
      dialogRef = this.#dialog.open(DialogConfirmDeleteComponent, {
        context: {
          nom: indice.nom,
          childrenText: `${nbrIndicateurs} indicateurs de performance liés`,
          typeElement: "l'indice",
        },
      });
    } else {
      dialogRef = this.#dialog.open(ConfirmDialogComponent, {
        context: {
          titre: `Supprimer l'indice`,
          contenu: `Êtes-vous sûr de vouloir supprimer l'indice ${indice.nom} ?`,
          confirmBtnText: `Confirmer la suppression`,
        },
      });
    }
    const toastrId = uuidv7();

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        this.#indiceStore.supprimer({
          idIndice: indice.id,
          next: () => {
            this.#phaseStore.charger(this.referentiel().id);
            toast.custom(ToastrSuccessComponent, {
              componentProps: { message: "Suppression de l'indice réussie", id: toastrId },
              id: toastrId,
            });
          },
        });
      }
    });
  }

  dropIndiceResultat(event: CdkDragDrop<IndiceRésultats[]>) {
    const referentiel = this.referentiel();
    if (!referentiel) return;

    if (event.previousIndex === event.currentIndex) return;

    const toastrId = uuidv7();
    let indices: IndiceRésultats[] = event.container.data;
    moveItemInArray(indices, event.previousIndex, event.currentIndex);

    const itemsToUpdate = indices.map((item, i) => ({ item, newIndice: i }));

    if (itemsToUpdate.length === 0) return;

    const idReferentiel = referentiel.id;

    const indicesData = itemsToUpdate.map((x) => {
      return {
        id: x.item.id,
        nom: x.item.nom,
        indice: x.newIndice,
      };
    });

    this.#indiceStore.modifier({
      data: {
        indices: indicesData,
        idAptitude: this.item().id,
      },
      next: () => {
        this.#indiceStore.charger(idReferentiel);
        toast.custom(ToastrSuccessComponent, {
          componentProps: { message: 'Réordonnancement des indices de résultats réussi', id: toastrId },
          id: toastrId,
        });
      },
    });
  }
}
